import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import type { AlertDialogProps, ConfirmDialogProps, UiDialogType } from "./types";

const DIALOG_STYLES: Record<
  UiDialogType,
  { icon: React.ComponentType<{ className?: string }>; iconClassName: string; buttonClassName: string }
> = {
  danger: {
    icon: XCircleIcon,
    iconClassName: "text-red-600 dark:text-red-400",
    buttonClassName: "bg-red-600 hover:bg-red-700",
  },
  success: {
    icon: CheckCircleIcon,
    iconClassName: "text-green-600 dark:text-green-400",
    buttonClassName: "bg-green-600 hover:bg-green-700",
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconClassName: "text-yellow-600 dark:text-yellow-400",
    buttonClassName: "bg-yellow-600 hover:bg-yellow-700",
  },
  info: {
    icon: InformationCircleIcon,
    iconClassName: "text-blue-600 dark:text-blue-400",
    buttonClassName: "bg-blue-600 hover:bg-blue-700",
  },
};

function getDialogStyle(type: UiDialogType) {
  return DIALOG_STYLES[type];
}

function DialogShell({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-[9999]">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 dark:bg-black/60" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              {children}
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export function ConfirmDialog({
  isOpen,
  title,
  message,
  type = "warning",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  surfaceClassName = "bg-white dark:bg-gray-800",
  titleClassName = "text-gray-900 dark:text-gray-50",
  descriptionClassName = "text-gray-500 dark:text-gray-400",
  cancelButtonClassName = "text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600",
  confirmButtonClassName,
}: ConfirmDialogProps) {
  const { icon: Icon, iconClassName, buttonClassName } = getDialogStyle(type);

  return (
    <DialogShell isOpen={isOpen} onClose={onCancel}>
      <Dialog.Panel className={`w-full max-w-md rounded-lg p-6 shadow-xl ${surfaceClassName}`}>
        <div className="flex items-start gap-4">
          <Icon className={`h-6 w-6 flex-shrink-0 ${iconClassName}`} />
          <div className="flex-1">
            <Dialog.Title className={`text-lg font-medium ${titleClassName}`}>{title}</Dialog.Title>
            <Dialog.Description className={`mt-2 text-sm ${descriptionClassName}`}>
              {message}
            </Dialog.Description>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className={`rounded-lg px-4 py-2 text-sm font-medium cursor-pointer ${cancelButtonClassName}`}
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white cursor-pointer ${confirmButtonClassName || buttonClassName}`}
          >
            {confirmText}
          </button>
        </div>
      </Dialog.Panel>
    </DialogShell>
  );
}

export function AlertDialog({
  isOpen,
  title,
  message,
  type = "info",
  confirmText = "Confirm",
  onConfirm,
  surfaceClassName = "bg-white dark:bg-gray-800",
  titleClassName = "text-gray-900 dark:text-gray-50",
  descriptionClassName = "text-gray-500 dark:text-gray-400",
  confirmButtonClassName,
}: AlertDialogProps) {
  const { icon: Icon, iconClassName, buttonClassName } = getDialogStyle(type);

  return (
    <DialogShell isOpen={isOpen} onClose={onConfirm}>
      <Dialog.Panel className={`w-full max-w-md rounded-lg p-6 shadow-xl ${surfaceClassName}`}>
        <div className="flex items-start gap-4">
          <Icon className={`h-6 w-6 flex-shrink-0 ${iconClassName}`} />
          <div className="flex-1">
            <Dialog.Title className={`text-lg font-medium ${titleClassName}`}>{title}</Dialog.Title>
            <Dialog.Description className={`mt-2 text-sm ${descriptionClassName}`}>
              {message}
            </Dialog.Description>
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white cursor-pointer ${confirmButtonClassName || buttonClassName}`}
          >
            {confirmText}
          </button>
        </div>
      </Dialog.Panel>
    </DialogShell>
  );
}
